<template>
  <button class="product-item" :class="{ 'popover-open': isPopoverVisible }" @click="$emit('click')">
    <div class="category-image" v-if="itemImage">
      <img :src="itemImage" alt="Product image" class="product-image" loading="lazy" decoding="async">
    </div>
    <div class="product-details">
      <span class="product-description">{{ description.slice(0, 13) + '...' }}</span>
      <div class=" align-items-center justify-content-between d-flex">
        <h5 class="product-price">{{ $utils.formatMoney(product.SellingIncl) }}</h5>
        <button
            class="btn btn-sm btn-outline-info info-icon"
            @click.stop.prevent="showPopover"
            ref="popoverTopBtn"
        >
          <font-awesome-icon icon="info-circle" />
        </button>
        <div @click.stop.prevent class="info-popover-container">
          <div
              ref="popoverTop"
              class="popover-content"
              :class="{ 'popover-visible': isPopoverVisible }"
          >
            <div class="popover-inner retail-details">
              <div class="detail-header">
                <div class="detail-title-wrap">
                  <h6 class="detail-title">{{ description }}</h6>
                  <span class="detail-subtitle" v-if="stockCode">Stock Code: {{ stockCode }}</span>
                </div>
                <button @click.stop.prevent="hidePopover" class="btn-close popover-close-btn"></button>
              </div>

              <div class="detail-row">
                <span class="detail-label">Price</span>
                <span class="detail-value price">{{ $utils.formatMoney(product.SellingIncl) }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Stock Status</span>
                <span class="stock-badge" :class="stockStatusClass">{{ stockStatusText }}</span>
              </div>

              <div v-if="description2" class="description-section">
                <h6 class="section-title">Description</h6>
                <p class="section-content">{{ description2 }}</p>
              </div>

              <div class="details-section">
                <div class="detail-item" v-if="stockCode">
                  <span class="detail-label">Product Code</span>
                  <span class="detail-value code">{{ stockCode }}</span>
                </div>
                <div class="detail-item" v-if="showQty && product.QTY > 0 && !isNonStockItem">
                  <span class="detail-label">Available Quantity</span>
                  <span class="detail-value">{{ $utils.formatQTY(product.QTY) }} units</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </button>
</template>

<script>
import { createPopper } from '@popperjs/core';
import { utils as $utils } from "@/utils/utils";

export default {
  name: "ProductItem",
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  computed: {
    description() {
      return $utils.toPascalCase(this.product.Description);
    },
    description2() {
      return $utils.toPascalCase(this.product.Description2);
    },
    showQty() {
      let securities = this.$store.getters.getSecurities;
      if (!securities) return false;
      return !securities.hideQuantity;
    },
    itemImage() {
      return this.product.Image || this.image;
    },
    isNonStockItem() {
      return Boolean(this.product?.StockSetting?.NonStock);
    },
    stockCode() {
      return this.product.PLU || this.product.Barcode || "";
    },
    stockStatusClass() {
      if (this.isNonStockItem) return "status-service";
      if (Number(this.product.QTY) > 0) return "status-in-stock";
      return "status-out-of-stock";
    },
    stockStatusText() {
      if (this.isNonStockItem) return "Service Item";
      if (Number(this.product.QTY) > 0) return "In Stock";
      return "Out of Stock";
    }
  },
  data() {
    return {
      textLength: 50,
      image: require('@/assets/img/no_image_picture.png'),
      isPopoverVisible: false,
      popperInstance: null
    };
  },
  beforeDestroy() {
    this.setPopoverHostLayer(false);
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
  },
  methods: {
    getScrollerItemView() {
      const root = this.$el;
      if (!root || typeof root.closest !== "function") return null;
      return root.closest(".vue-recycle-scroller__item-view");
    },
    setPopoverHostLayer(isOpen) {
      const itemView = this.getScrollerItemView();
      if (!itemView) return;
      itemView.style.zIndex = isOpen ? "2000" : "";
    },
    showPopover() {
      this.$emit('hide-all-popovers');
      if (this.isPopoverVisible) {
        this.hidePopover();
        return;
      }
      this.isPopoverVisible = true;
      this.setPopoverHostLayer(true);
      this.createPopperInstance();
    },
    hidePopover() {
      this.isPopoverVisible = false;
      this.setPopoverHostLayer(false);
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
    },
    createPopperInstance() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
      }

      this.popperInstance = createPopper(this.$refs.popoverTopBtn, this.$refs.popoverTop, {
        placement: 'right',
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8], // 8 pixels from the trigger element
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
            },
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.info-popover-container {
  position: relative;
  z-index: 1000; // Ensure info button is above other elements
}
.info-icon {
  margin-left: 8px;
  font-size: 0.7rem;
}

.popover-content {
  position: absolute;
  visibility: hidden;
  z-index: 1100;

  background-color: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  color: var(--text-primary);

  width: 16rem;
  max-width: 18rem;
  padding: 0.75rem;
  transition: var(--transition-default);
}

.popover-visible {
  visibility: visible;
}

.popover-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  z-index: 2;

  .popover-close-btn {
    align-self: flex-end;
    width: 1.2rem;
    height: 1.2rem;
    background-color: transparent;
    filter: var(--close-btn-filter, none);
  }

}

.retail-details {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }

  .detail-title-wrap {
    min-width: 0;
  }

  .detail-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.2;
  }

  .detail-subtitle {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: var(--text-secondary);
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid var(--card-border);
  }

  .detail-label {
    font-size: 11px;
    color: var(--text-secondary);
  }

  .detail-value {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .detail-value.price {
    color: var(--accent-primary);
  }

  .stock-badge {
    padding: 2px 7px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
  }

  .stock-badge.status-in-stock {
    background: var(--success, #28a745);
  }

  .stock-badge.status-service {
    background: var(--accent-primary, #0d6efd);
  }

  .stock-badge.status-out-of-stock {
    background: var(--danger, #dc3545);
  }

  .description-section {
    margin-top: 8px;
  }

  .section-title {
    margin: 0 0 4px 0;
    font-size: 11px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .section-content {
    margin: 0;
    font-size: 11px;
    line-height: 1.3;
    color: var(--text-secondary);
  }

  .details-section {
    margin-top: 8px;
    padding: 6px 8px;
    border-radius: 8px;
    background: var(--secondary-button-bg, #f6f7f8);
  }

  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .detail-item + .detail-item {
    margin-top: 4px;
  }

  .detail-value.code {
    font-family: "Courier New", monospace;
    font-size: 11px;
  }
}

.product-item {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  z-index: 1;
  align-items: center;
  border-radius: var(--button-radius);
  box-shadow: var(--card-shadow);
  background-color: var(--card-bg);
  text-align: center;
  width: 100%;
  max-width: 120px;
  min-width: 0;
  height: 120px;
  box-sizing: border-box;
  transition: var(--transition-default);
  border: var(--card-border);
  
  &:hover, &:focus {
    background: var(--accent-primary);
    color: white;
    transform: var(--hover-scale);
    box-shadow: 0 4px 10px rgba(var(--accent-primary-rgb), 0.4);

    .wrapper {
      .product-description, .product-price {
        color: white;
      }
    }
  }
  
  &.popover-open {
    z-index: 30;

    &:hover, &:focus {
      background: var(--card-bg);
      color: var(--text-primary);
      transform: scale(1);

      .wrapper {
        .product-description, .product-price {
          color: var(--text-primary);
        }
      }
    }
  }

  .category-image {
    width: 100%;
    display: flex;
    justify-content: center;

    .product-image {
      margin-top: 5px;
      width: 45px;
      height: 45px;
      object-fit: cover;
      border-radius: 5px;
    }
  }

  .product-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    //margin-top: 5px;

    .product-description {
      font-size: 14px;
      font-weight: var(--font-weight-buttons);
      color: var(--text-primary);
      word-wrap: break-word;
      line-height: 1.2;
      margin-top: 1px;
      transition: var(--transition-default);
    }

    .product-description2 {
      font-size: 10px;
      font-weight: var(--font-weight-body);
      color: var(--text-secondary);
      transition: var(--transition-default);
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;

      .product-status {
        font-size: 8px;
        padding: 2px 8px;
        border-radius: var(--button-radius);
        font-weight: var(--font-weight-buttons);
        transition: var(--transition-default);

        &.info {
          background: rgba(var(--accent-primary-rgb), 0.2);
          color: var(--accent-primary);
        }

        &.danger {
          background: rgba(var(--danger-rgb), 0.2);
          color: var(--danger);
        }
      }
    }

    .product-price {
      font-size: 14px;
      font-weight: var(--font-weight-headings);
      color: var(--text-primary);
      transition: var(--transition-default);
    }
  }
}

</style>
